import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { filter, pairwise, startWith } from 'rxjs';

import { DropdownObject } from '@inmoment/the-kitchen';

import { FilterTypeIDs } from 'app/constants/constants';
import { GroupService } from 'app/providers/semantria-api/group.service';

import { Field, FieldService, FieldType } from 'app/providers/storage-api/field.service';
import { Project } from 'app/providers/storage-api/project.service';

export interface SelectedFilterType {
  id: FilterTypeIDs;
  label: string;
  data: DropdownObject[];
  selected: string[];
}

@Component({
  selector: 'app-field-list-filter',
  templateUrl: './field-list-filter.component.html',
  styleUrl: './field-list-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class FieldListFilterComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly translocoService = inject(TranslocoService);
  private readonly fieldService = inject(FieldService);
  private readonly groupService = inject(GroupService);

  readonly fields = input<Field[]>([]);
  readonly unusedFields = input<Field[]>([]);
  readonly projects = input<Project[]>([]);
  readonly popoutOpen = input<boolean>(false);
  readonly popoutClosed = output<any>();

  readonly filteredFields = signal<Field[]>([]);
  readonly filterOptions = signal<SelectedFilterType[]>(
    JSON.parse(JSON.stringify(this.fieldService.getSelectedFilterOptions()))
  );
  readonly projectFieldsLoading = signal(false);
  readonly areFiltersApplied = computed(
    () => this.filterOptions().filter((f) => f.selected.length > 0).length > 0
  );

  private readonly fieldTypes: FieldType[] = [
    'alias',
    'calculated',
    'date',
    'double',
    'keyword',
    'long',
  ];

  constructor() {
    this.destroyRef.onDestroy(() => this.fieldService.setSelectedFilterOptions([]));
    this.setupTranslations();
    this.setupFieldsChange();
    this.setupProjectsChange();
    this.setupPopoutOpenChange();
  }

  private setupTranslations(): void {
    this.translocoService
      .selectTranslateObject('field-list-filters')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((t) => {
        const labels = [
          t['project'],
          t['field-type'],
          t['filterable'],
          t['in-use'],
          t['contains-pii'],
          t['locked'],
          t['used-as-id-field'],
        ];

        const updatedOptions = this.filterOptions().map((filterOption, index) => {
          const updated = { ...filterOption, label: labels[index] };
          switch (updated.id) {
            case 'project':
              break;
            case 'fieldType':
              updated.data = this.fieldTypes.map((field) => ({
                id: field,
                title: t['field-types'][field],
              }));
              break;
            default:
              updated.data = [
                { id: 'yes', title: t['yes'] },
                { id: 'no', title: t['no'] },
              ];
          }
          return updated;
        });
        this.filterOptions.set(updatedOptions);
        this.fieldService.setSelectedFilterOptions(updatedOptions);
      });
  }

  private setupFieldsChange(): void {
    toObservable(this.fields)
      .pipe(
        startWith(undefined),
        pairwise(),
        filter(([prev]) => prev !== undefined),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.filterFields(true));
  }

  private setupProjectsChange(): void {
    toObservable(this.projects)
      .pipe(
        filter((projects) => projects?.length > 0),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((projects) => {
        const updatedOptions = this.filterOptions().map((ft) =>
          ft.id === 'project'
            ? { ...ft, data: projects.map((p) => ({ id: p.id, title: p.name })) }
            : ft
        );
        this.filterOptions.set(updatedOptions);
        this.fieldService.setSelectedFilterOptions(updatedOptions);
      });
  }

  private setupPopoutOpenChange(): void {
    toObservable(this.popoutOpen)
      .pipe(
        filter((open) => open),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.filterOptions.set(
          JSON.parse(JSON.stringify(this.fieldService.getSelectedFilterOptions()))
        );
      });
  }

  private async filterFields(emit = false): Promise<void> {
    let filtered = [...this.fields()];
    for (const ft of this.filterOptions()) {
      if (!['project', 'collection', 'fieldType'].includes(ft.id)) {
        filtered = this.filterBooleanValues(ft, filtered);
      } else if (ft.id === 'fieldType') {
        filtered = this.filterFieldTypes(ft, filtered);
      } else if (ft.id === 'project') {
        filtered = await this.filterProjects(ft, filtered);
      }
    }
    this.filteredFields.set(filtered);
    //If filtering is the result of a CRUD action (eg: new field added)
    if (emit) {
      this.popoutClosed.emit({
        filteredFields: this.filteredFields(),
        areFiltersApplied: this.areFiltersApplied(),
        onlyUnusedFieldsApplied: this.getOnlyUnusedFieldsApplied(),
      });
    }
  }

  onFilterChange(selected: DropdownObject[], id: string): void {
    const updatedOptions = this.filterOptions().map((f) =>
      f.id === id ? { ...f, selected: selected.map((s) => s.id) } : f
    );
    this.filterOptions.set(updatedOptions);
    this.filterFields();
  }

  private filterBooleanValues(selectedFilterType: SelectedFilterType, filtered: Field[]): Field[] {
    const selectedValues = selectedFilterType.selected.map((id) => id === 'yes');
    //If none or both boolean values are selected, don't filter
    if (selectedValues.length !== 1) {
      return filtered;
    }
    switch (selectedFilterType.id) {
      case 'filterable':
        return filtered.filter((f) => !!f.metadata?.filterable === selectedValues[0]);
      case 'containsPII':
        return filtered.filter((f) => !!f.metadata?.redacted === selectedValues[0]);
      case 'locked':
        return filtered.filter((f) => !!f.locked === selectedValues[0]);
      case 'usedAsIdField':
        return filtered.filter((f) => !!f.identityField === selectedValues[0]);
      case 'inUse':
        return filtered.filter((f) =>
          selectedValues[0] === true
            ? !this.unusedFields().find((uf) => uf.id === f.id)
            : this.unusedFields().find((uf) => uf.id === f.id)
        );
      default:
        return filtered;
    }
  }

  private filterFieldTypes(selectedFilterType: SelectedFilterType, filtered: Field[]): Field[] {
    if (selectedFilterType.selected.length === 0) {
      return filtered;
    }
    return filtered.filter((f) => {
      for (const selected of selectedFilterType.selected) {
        if (selected === 'alias' && f.alias) {
          return true;
        }
        if (f.fieldType === selected) {
          return true;
        }
      }
      return false;
    });
  }

  private async filterProjects(
    selectedFilterType: SelectedFilterType,
    filtered: Field[]
  ): Promise<Field[]> {
    if (selectedFilterType.selected.length === 0) {
      return filtered;
    }
    this.projectFieldsLoading.set(true);
    const projectFields = (
      await this.fieldService.getProjectFields(
        this.groupService.getSelectedGroupId(),
        selectedFilterType.selected
      )
    ).items;

    const result = filtered.filter((f) => projectFields.includes(f.fieldName));
    this.projectFieldsLoading.set(false);
    return result;
  }

  onResetAllFilters(): void {
    const resetOptions = this.filterOptions().map((f) => ({ ...f, selected: [] }));
    this.filterOptions.set(resetOptions);
    this.filteredFields.set([...this.fields()]);
    this.fieldService.setSelectedFilterOptions(resetOptions);
    this.popoutClosed.emit({
      filteredFields: null,
      areFiltersApplied: false,
      onlyUnusedFieldsApplied: false,
    });
  }

  onCancel(): void {
    this.popoutClosed.emit(null);
  }

  onApplyFilters(): void {
    this.popoutClosed.emit({
      filteredFields: this.filteredFields(),
      areFiltersApplied: this.areFiltersApplied(),
      onlyUnusedFieldsApplied: this.getOnlyUnusedFieldsApplied(),
    });
    this.fieldService.setSelectedFilterOptions(this.filterOptions());
  }

  private getOnlyUnusedFieldsApplied(): boolean {
    return (
      this.filterOptions().filter(
        (f) => f.id === 'inUse' && f.selected.length === 1 && f.selected[0] === 'no'
      ).length === 1
    );
  }
}
